import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Package } from 'lucide-react';

const Products = () => {
  const products = [
    {
      title: 'BOP Control Systems',
      description: 'Precision-engineered blowout preventer control systems for critical oilfield safety applications.',
      link: '/products/bop-systems',
      image: 'https://images.unsplash.com/photo-1581092160562-40aa08e78837?w=600&q=80',
    },
    {
      title: 'Frac Accumulator Systems',
      description: 'High-pressure hydraulic accumulator systems designed for fracturing operations.',
      link: '/products/frac-systems',
      image: 'https://images.unsplash.com/photo-1581092918056-0c4c3acd65d4?w=600&q=80',
    },
    {
      title: 'Depurators',
      description: 'Custom-fabricated depuration systems for fluid separation and processing applications.',
      link: '/products/depurators',
      image: 'https://images.unsplash.com/photo-1581092160607-ee22621dd758?w=600&q=80',
    },
    {
      title: 'Oilfield Skids and Fabrication',
      description: 'Complete turnkey skid packages and custom fabrication for oilfield operations.',
      link: '/products/oilfield-skids',
      image: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=600&q=80',
    },
    {
      title: 'Structural Weldments and Cages',
      description: 'Heavy-duty structural weldments, frames, and protective cages for industrial equipment.',
      link: '/products/structural-weldments',
      image: 'https://images.unsplash.com/photo-1581092921461-eab62e97a780?w=600&q=80',
    },
    {
      title: 'Cabinets & Sheetmetal Work',
      description: 'Precision sheet metal fabrication including enclosures, cabinets, and custom housings.',
      link: '/products/cabinets',
      image: 'https://images.unsplash.com/photo-1581092918484-8313e1f7e8c7?w=600&q=80',
    },
    {
      title: 'Spools and Pressure Piping',
      description: 'Custom pipe spools and high-pressure piping systems for process applications.',
      link: '/products/spools',
      image: 'https://images.unsplash.com/photo-1581092795360-fd1ca04f0952?w=600&q=80',
    },
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="products-page">
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 overflow-hidden" data-testid="products-hero">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1581092918056-0c4c3acd65d4?w=1920&q=80"
            alt="Our Products"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 to-indigo-900/85"></div>
        </div>
        <div className="max-w-7xl mx-auto relative z-10">
          <h1 className="text-4xl sm:text-5xl font-bold text-white heading-font mb-6" data-testid="products-heading">
            Our Products
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl">
            Precision-engineered products and custom fabrication solutions for demanding industrial applications.
          </p>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white" data-testid="products-grid">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product, index) => (
              <Link
                key={index}
                to={product.link}
                className="group bg-white rounded-2xl overflow-hidden shadow-md card-hover border border-gray-100"
                data-testid={`product-card-${index}`}
              >
                <div className="aspect-[4/3] overflow-hidden bg-gray-100">
                  <img
                    src={product.image}
                    alt={product.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 heading-font mb-3">{product.title}</h3>
                  <p className="text-gray-600 text-sm mb-4 leading-relaxed">{product.description}</p>
                  <div className="flex items-center text-blue-700 font-semibold group-hover:text-blue-800">
                    Learn More
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50" data-testid="custom-fabrication-section">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="flex-shrink-0">
                <div className="w-20 h-20 bg-blue-100 rounded-2xl flex items-center justify-center">
                  <Package className="w-10 h-10 text-blue-700" />
                </div>
              </div>
              <div className="flex-1">
                <h2 className="text-3xl font-bold text-gray-900 heading-font mb-4">Custom Product Development</h2>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Don't see what you need? We specialize in custom product development and fabrication. Our engineering team works with you to design and manufacture products tailored to your specific requirements.
                </p>
                <Link
                  to="/contact"
                  className="inline-flex items-center px-6 py-3 bg-blue-700 text-white rounded-lg hover:bg-blue-800 font-semibold transition-all shadow-md"
                  data-testid="custom-product-cta"
                >
                  Discuss Your Project
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Products;